import {useState, useEffect, useCallback} from 'react';
import {Link} from 'react-router';
import {Spinner} from 'react-bootstrap';
import DateRangePicker from '../components/DateRangePicker';
import dayjs from 'dayjs';

import * as AppointmentApi from '../api/AppointmentApi';
import * as ClinicApi from '../api/ClinicApi';
import {getAppointmentStatusDisplay} from '../utils/timeSlotUtils';
import {useAuth} from '../hooks/AuthContext';
import PageShell from '../components/PageShell';
import ConfirmModal from '../components/ConfirmModal';
import './Appointments.css';

export default function Appointments() {
  const auth = useAuth();

  const [appointments, setAppointments] = useState([]);
  const [clinicAppointments, setClinicAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sorting & Filtering
  const [sortOrder, setSortOrder] = useState('descending');
  const [dateRange, setDateRange] = useState([
    new Date(dayjs().subtract(1, 'month').format('YYYY-MM-DD')),
    new Date(dayjs().add(1, 'month').format('YYYY-MM-DD')),
  ]);

  const [error, setError] = useState(null);

  // Delete state
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let clinicFailed = false;
      const [oldAppts, newAppts] = await Promise.all([
        AppointmentApi.getAllAppointment(sortOrder, [
          dayjs(dateRange[0]).format('YYYY-MM-DD'),
          dayjs(dateRange[1]).format('YYYY-MM-DD'),
        ]).catch((err) => {
          console.error('Failed to fetch old appointments:', err);
          return [];
        }),
        ClinicApi.getAppointmentRequests({
          patientId: auth.user.id,
        }).catch((err) => {
          console.warn('Failed to fetch clinic appointments:', err);
          clinicFailed = true;
          return [];
        }),
      ]);

      setAppointments(oldAppts);
      setClinicAppointments(newAppts);
      if (clinicFailed) setError('Unable to load all of your appointments right now. Please try again.');

      if (oldAppts.length === 0 && newAppts.length === 0) {
        // We'll let the UI render the empty state gracefully without an explicit error banner
        // unless they both explicitly failed in a way we want to show.
      }
    } catch (err) {
      console.error('Failed to fetch appointments:', err);
      setError('Unable to load appointments at this time. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [auth.user.id, dateRange, sortOrder]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const handleConfirmTransfer = async (appointmentId, confirmed) => {
    try {
      await ClinicApi.confirmAppointmentTransfer(appointmentId, confirmed);
      fetchAppointments();
      alert(confirmed ? 'Transfer confirmed!' : 'Transfer rejected');
    } catch (err) {
      alert('Failed to process transfer');
    }
  };

  const showDeleteConfirm = (id) => {
    setAppointmentToDelete(id);
    setDeleteModalShow(true);
  };

  const handleDeleteConfirm = () => {
    if (!appointmentToDelete) return;
    AppointmentApi.deleteAppointment(appointmentToDelete)
      .then(() => {
        setClinicAppointments((prev) => prev.filter((appt) => appt.id !== appointmentToDelete));
        setAppointments((prev) => prev.filter((appt) => appt.id !== appointmentToDelete));
        setDeleteModalShow(false);
        setAppointmentToDelete(null);
      })
      .catch((err) => {
        console.error('Failed to delete appointment:', err);
        alert('Failed to delete appointment');
      });
  };

  const renderClinicAppointment = (appointment) => {
    const statusDisplay = getAppointmentStatusDisplay(appointment.status);

    const reminderAlerts = appointment.reminder_alerts || [];
    const sentCount = reminderAlerts.filter((a) => a.delivery_status === 'sent').length;
    const pendingCount = reminderAlerts.filter((a) => a.delivery_status === 'pending').length;
    const failedCount = reminderAlerts.filter(
      (a) => a.delivery_status === 'failed' && !(a.error_message || '').includes('Skipped'),
    ).length;
    const skippedCount = reminderAlerts.filter(
      (a) => a.delivery_status === 'failed' && (a.error_message || '').includes('Skipped'),
    ).length;

    // Badges based on original mapping
    let badgeClass = 'appt-badge-primary';
    if (statusDisplay.color === 'success') badgeClass = 'appt-badge-success';
    if (statusDisplay.color === 'warning') badgeClass = 'appt-badge-warning';
    if (statusDisplay.color === 'danger') badgeClass = 'appt-badge-danger';
    if (statusDisplay.color === 'info') badgeClass = 'appt-badge-info';

    return (
      <div key={appointment.id} className="appt-card">
        <div className="appt-card-top">
          <div className="appt-date-block">
            <div className="appt-date-calendar">
              <span className="appt-date-m">{dayjs(appointment.start_time).format('MMM')}</span>
              <span className="appt-date-d">{dayjs(appointment.start_time).format('DD')}</span>
            </div>
            <div className="appt-time-doc">
              <h3>
                {dayjs(appointment.start_time).format('HH:mm')} -{' '}
                {dayjs(appointment.end_time).format('HH:mm')}
              </h3>
              <p>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="12"
                    cy="7"
                    r="4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {appointment.doctor_name || (appointment.legacy ? 'Personal reminder' : 'To be assigned')}
              </p>
            </div>
          </div>
          <div className={`appt-badge ${badgeClass}`}>
            {statusDisplay.icon} {statusDisplay.label}
          </div>
        </div>

        {appointment.auto_assigned && <div className="appt-badge appt-badge-info mb-2">Auto-assigned</div>}

        {reminderAlerts.length > 0 && (
          <div style={{display: 'flex', gap: '6px', marginBottom: '8px'}}>
            {sentCount > 0 && <span className="appt-badge appt-badge-success">🔔 {sentCount} Sent</span>}
            {pendingCount > 0 && (
              <span className="appt-badge appt-badge-warning">⏳ {pendingCount} Pending</span>
            )}
            {failedCount > 0 && <span className="appt-badge appt-badge-danger">❌ {failedCount} Failed</span>}
            {skippedCount > 0 && (
              <span className="appt-badge" style={{backgroundColor: '#E5E7EB', color: '#4B5563'}}>
                ⏭️ {skippedCount} Skipped
              </span>
            )}
          </div>
        )}

        {appointment.status === 'transferred' && !appointment.patient_confirmed_transfer && (
          <div className="appt-transfer-box">
            <p>
              <strong>Transfer Request</strong>
              <br />
              From: {appointment.transfer_from_doctor_name} → To: {appointment.doctor_name}
              <br />
              Reason: {appointment.transfer_reason}
            </p>
            <div style={{display: 'flex', gap: '8px'}}>
              <button
                className="appt-btn-solid"
                style={{background: '#059669'}}
                onClick={() => handleConfirmTransfer(appointment.id, true)}
              >
                Accept
              </button>
              <button
                className="appt-btn-solid"
                style={{background: '#DC2626'}}
                onClick={() => handleConfirmTransfer(appointment.id, false)}
              >
                Reject
              </button>
            </div>
          </div>
        )}

        <div
          className="appt-actions"
          style={{display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center'}}
        >
          <Link
            to={`/appointments/${appointment.id}/edit`}
            className="appt-action-icon"
            title="Edit"
            style={{
              color: '#6B7280',
              display: 'flex',
              alignItems: 'center',
              padding: '8px',
              borderRadius: '8px',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
          <button
            className="appt-action-icon"
            onClick={() => showDeleteConfirm(appointment.id)}
            title="Delete"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#DC2626',
              display: 'flex',
              alignItems: 'center',
              padding: '8px',
              borderRadius: '8px',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <Link
            to={`/appointments/${appointment.id}`}
            className="appt-btn-solid"
            style={{marginLeft: 'auto'}}
          >
            View Details
          </Link>
        </div>
      </div>
    );
  };

  return (
    <PageShell title="Appointments" showBackButton={true}>
      <div className="appts-container">
        <div className="appts-header">
          <h1 className="appts-title">My Appointments</h1>
          <Link to="./add" className="appts-add-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 5v14M5 12h14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Add Reminder
          </Link>
        </div>

        <div className="appts-filters">
          <div className="appts-segmented">
            <button
              className={`appts-seg-btn ${sortOrder === 'descending' ? 'active' : ''}`}
              onClick={() => setSortOrder('descending')}
            >
              Recent First
            </button>
            <button
              className={`appts-seg-btn ${sortOrder === 'ascending' ? 'active' : ''}`}
              onClick={() => setSortOrder('ascending')}
            >
              Oldest First
            </button>
          </div>

          <div className="appts-date-picker">
            <DateRangePicker value={dateRange} onChange={setDateRange} format="y-MM-dd" clearIcon={null} />
          </div>
        </div>

        {loading ? (
          <div style={{textAlign: 'center', padding: '40px 0'}}>
            <Spinner animation="border" role="status" style={{color: '#C43B71'}}>
              <span className="visually-hidden">Loading appointments…</span>
            </Spinner>
          </div>
        ) : (
          <>
            {clinicAppointments.length > 0 && (
              <div className="appts-list">
                <h2 className="appts-section-title">New System Appointments</h2>
                {clinicAppointments.map(renderClinicAppointment)}
              </div>
            )}

            {appointments.length > 0 && (
              <div className="appts-list">
                <h2 className="appts-section-title">Legacy Appointments</h2>
                {/* Legacy calendar entries are normalized to the same shape and edited through
                    the same /appointments/:id/edit flow (PATCH /appointment-requests/:id). */}
                {appointments.map(renderClinicAppointment)}
              </div>
            )}

            {error ? (
              <div role="alert" style={{textAlign: 'center', padding: '60px 0', color: '#B91C1C'}}>
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{marginBottom: '16px', opacity: 0.8}}
                >
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                    fill="currentColor"
                  />
                </svg>
                <p style={{margin: 0, fontSize: '15px'}}>{error}</p>
                <button
                  type="button"
                  className="appt-btn-solid"
                  style={{marginTop: 16}}
                  onClick={fetchAppointments}
                >
                  Try again
                </button>
              </div>
            ) : appointments.length === 0 && clinicAppointments.length === 0 ? (
              <div style={{textAlign: 'center', padding: '60px 0', color: '#6B7280'}}>
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{marginBottom: '16px', opacity: 0.5}}
                >
                  <path
                    d="M19 4h-2V2h-2v2H9V2H7v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10z"
                    fill="currentColor"
                  />
                </svg>
                <p style={{margin: 0, fontSize: '15px'}}>No appointments found for the selected dates.</p>
              </div>
            ) : null}
          </>
        )}
      </div>

      <ConfirmModal
        show={deleteModalShow}
        onHide={() => setDeleteModalShow(false)}
        modalTitle="Delete Appointment"
        btnText="Delete"
        clickBtn={handleDeleteConfirm}
        btnClass="btn-danger"
      >
        <p>Are you sure you want to delete this appointment reminder?</p>
      </ConfirmModal>
    </PageShell>
  );
}
